/* eslint-disable no-use-before-define */
import { put, takeEvery, all, call, select } from 'redux-saga/effects';
import { setAction as setChartAction } from '../actions/chartEditor';
import { setAction as setProjectConfigAction } from '../../../redux/actions/projectConfig';
import { updateChartCountAction } from '../../../redux/actions/profile';
import { getAppConfig, getProfileConfig } from '../../../redux/selectors/profile';
import actionTypes from '../../../redux/actions/action-types';
import { getProjectConfig } from '../../../redux/selectors/projectConfig';
import { getAggregatedChartConfig, getChartConfig } from '../selectors/chartEditor';
import StockTools from '../../../editor/core/highed.stocktools';
import Inline from './ChartEditorInline.js';
import { snackBar, merge, setAttr } from 'highed';
import { loadData, addBlankSeries } from './ChartEditorData';
import { loadMap, loadMapCodeNameMapping } from './ChartEditorMap';
import { isArray, isString, isObject, isFunction, cloneDeep } from 'lodash';
import DataCollection from '../../../shared/editor/DataCollection';
import {
  getChartWithChartidAndVersionid,
  getTeamChartWithTeamidAndChartidAndVersionid,
  getTeamChartWithTeamidAndChartid,
  postTeamChartUsingTeamid,
  postTeamChartUsingTeamidAndChartid,
  getShowChartWithUuid,
  postTeamChartEmbedUsingTeamidAndChartidAndVersion
} from '../../../api/cloudApiGenerated';
import {
  fixPlotLinesAndBands,
  setGlobalLang,
  loadTooltip
} from '../../../editor/ui/chartpreview/highed.chartpreview.utils';
import { parseCSV } from '../../../editor/core/highcharts-editor';
import { generateEditorSettings } from './../../../shared/editor/EditorHelper';
import { setCustomCode } from './ChartEditorCustomize';
import DefaultData from '../meta/data/DefaultData';
import Inspector from 'editor/ui/highed.inspector';
import { getSeriesType } from 'shared/wizard/utils/seriesHelper.tsx';
import { getTemplateData } from './ChartEditorTemplates';
import { initThemeEditor, loadTheme } from 'pages/ThemeEditorPage/middleware/ThemeEditor';
import highchartsErrors from './../../../editor/meta/errors';
import {
  getTeam,
  loadRelevantModules,
  makeEmbedDetails,
  fixSeriesMapping,
  getHighchartsResponsiveConfig
} from './ChartEditorHelper';

let stockTools = StockTools(true);

export function* publishChart(params) {
  const { inspired } = params.data;

  const chartConfig = yield select(getChartConfig);
  const profileConfig = yield select(getProfileConfig);
  const projectConfig = yield select(getProjectConfig);
  const { chartId, chartVersion, dataId, referenced, live, passwordProtected, localTeam } = chartConfig;
  const { team: globalTeam, user } = profileConfig;

  const team = getTeam(localTeam, globalTeam);
  const anonymous = !user && !team;

  if (anonymous) {
    let project = {};
    project = getAggregatedChartConfig(projectConfig, chartConfig);

    const body = {
      data: JSON.stringify(project),
      dataId,
      referenced,
      live
    };

    yield put(
      setChartAction({
        editorChartConfig: body,
        modalOpen: true
      })
    );

    return;
  }

  yield put(
    setProjectConfigAction({
      publishLoading: true
    })
  );

  let body = {
    inspired
  };

  if (passwordProtected.enabled) {
    body.private = true;
    body.password = passwordProtected.password;
  }

  try {
    const data = yield call(postTeamChartEmbedUsingTeamidAndChartidAndVersion, team.id, chartId, chartVersion, body);

    let embedDetails = makeEmbedDetails(data);
    yield all([
      put(
        setChartAction({
          query: embedDetails.injectCode
        })
      ),
      put(
        setProjectConfigAction({
          publishLoading: false,
          published: true,
          lastPublishTime: data.last_publish_time,
          embedDetails
        })
      )
    ]);
  } catch (e) {
    throw new Error(e);
  }
}

export function* saveChart(params) {
  let { reload, callback, body, goToPath, history, hideNotification } = params.data;

  const profileConfig = yield select(getProfileConfig);
  const chartConfig = yield select(getChartConfig);
  const { chartId, chartOwner, dataId, referenced, live, localTeam } = chartConfig;
  const appConfig = yield select(getAppConfig);
  const projectConfig = yield select(getProjectConfig);

  const { paths } = appConfig;
  const { division, team: globalTeam, user } = profileConfig;
  let { projectName } = projectConfig;

  const anonymous = !user && !team;
  const team = getTeam(localTeam, globalTeam);

  const teamId = anonymous ? undefined : team.id;
  let isNewChart = false;

  if (!body) {
    let project = {};
    project = getAggregatedChartConfig(projectConfig, chartConfig);
    let titleValue = projectName;

    if (!projectName) {
      if (project?.options?.title?.text) {
        titleValue = project?.options?.title?.text;
      } else titleValue = 'Untitled chart';
    }

    body = {
      data: JSON.stringify(project),
      dataId,
      referenced,
      live
    };
    if (titleValue !== 'Untitled chart') body.name = titleValue;
  }

  if (!anonymous && !hideNotification) snackBar('Saving chart');

  let saveChart;
  let parameters = [];

  if (anonymous) {
    // Anon chart, pop up modal
    yield put(
      setChartAction({
        editorChartConfig: body,
        signupModalOpen: true
      })
    );

    return;
  } else if (!chartId || chartOwner !== teamId) {
    isNewChart = true;
    if (division && division.id !== null) body.division_id = division.id;
    saveChart = postTeamChartUsingTeamid;
    parameters = [teamId, body];
  } else {
    saveChart = postTeamChartUsingTeamidAndChartid;
    parameters = [teamId, chartId, body];
  }

  let data = false;

  try {
    data = yield call(saveChart, ...parameters);
  } catch (error) {
    if (error && error.response && error.response.message && error.response.message[0] === 'Could not create chart') {
      snackBar('Sorry, you have run out of projects');
    }
    return error;
  }

  if (!hideNotification) snackBar('Chart saved');
  if (isNewChart) yield put(updateChartCountAction());

  if (chartId) {
    if (!reload) {
      window.history.pushState(null, null, '/edit/' + chartId);

      yield all([
        put(
          setChartAction({
            chartVersion: data.version
          })
        ),
        put(
          setProjectConfigAction({
            changeMade: false,
            lastSaved: new Date()
          })
        )
      ]);

      if (callback) return callback();
    } else {
      yield all([
        put(
          setChartAction({
            chartVersion: data.version
          })
        ),
        put(
          setProjectConfigAction({
            changeMade: false,
            lastSaved: new Date()
          })
        )
      ]);

      let newPath = paths.editChartLatest.replace(':chartid', chartId);
      history.push(newPath);
      if (callback) {
        return callback();
      }
    }
  } else if (!chartId || anonymous) {
    if (reload) {
      yield all([
        put(
          setChartAction({
            chartId: data.chart_id
          })
        ),
        put(
          setProjectConfigAction({
            changeMade: false,
            lastSaved: new Date()
          })
        )
      ]);

      let newPath = paths.editChartLatest.replace(':chartid', data.chart_id);

      let newPathObj = {
        pathname: newPath,
        state: {
          from: history.location.pathname
        }
      };

      if (goToPath) {
        if (isObject(goToPath)) {
          newPathObj = {
            ...newPathObj,
            ...goToPath
          };
        } else newPathObj.pathname = newPath;
      }

      history.push(newPathObj);
    } else {
      window.history.pushState(null, null, '/edit/' + data.chart_id);

      yield all([
        put(
          setChartAction({
            chartVersion: 1,
            chartId: data.chart_id,
            chartOwner: data.team_owner
          })
        ),
        put(
          setProjectConfigAction({
            saved: true,
            uuid: data.uuid,
            changeMade: false,
            lastSaved: new Date()
          })
        )
      ]);

      if (callback) return callback();
    }
  }
}

export function* loadChart(params) {
  const { history, chartId: id } = params.data;
  const chartConfig = yield select(getChartConfig);
  const profileConfig = yield select(getProfileConfig);
  const appConfig = yield select(getAppConfig);

  const { paths } = appConfig;
  const { anonymous, localTeam, chartVersion, dataCollection } = chartConfig;
  const { team: globalTeam } = profileConfig;
  const team = getTeam(localTeam, globalTeam);

  let getChart;
  if (anonymous) getChart = getChartWithChartidAndVersionid;
  else if (chartVersion) getChart = getTeamChartWithTeamidAndChartidAndVersionid;
  else getChart = getTeamChartWithTeamidAndChartid;

  let chartId = chartConfig.chartId || id;
  try {
    const data = yield call(getChart, ...(anonymous ? [chartId, 1] : [team.id, chartId, chartVersion]));
    dataCollection.updateData(data.data);

    yield all([
      put(
        setChartAction({
          ...data,
          chartOwner: data.team_owner,
          chartData: data,
          dataset: data.details
        })
      ),
      put(
        setProjectConfigAction({
          saved: true,
          published: data.has_published,
          lastPublishTime: data.last_publish_time,
          embedDetails: {
            injectCode: data.injectCode,
            iframeCode: data.embedCode,
            shareUrl: data.shareURL,
            socialLinks: data.shareLink,
            iframeURL: data.iframeURL
          },
          uuid: data.uuid,
          changeMade: false,
          lastSaved: data.creation_time,
          projectName: data.name
        })
      )
    ]);

    let project = JSON.parse(data.data);
    if (data.details) {
      const dataset = JSON.parse(data.details);
      project.settings.dataProvider = dataset;
    }

    if (data.mapData) {
      project.options.chart.map = JSON.parse(data.mapData);
      project.options.chart.map.hasOldMapData = true;
    }

    yield call(loadProject, {
      data: {
        project
      }
    });
    // if (cb) return cb();
  } catch (error) {
    console.log(error);
    history.push({
      pathname: paths.login
    });
  }
}

export function* loadChartFromUUID(params) {
  const { uuid } = params.data;
  const chartConfig = yield select(getChartConfig);

  const { dataCollection } = chartConfig;
  const id = uuid;

  try {
    const data = yield call(getShowChartWithUuid, id);
    if (!data.error) {
      dataCollection.updateData(JSON.parse(data.details));

      yield put(
        setChartAction({
          data: data.data,
          dataset: data.details,
          showChartWizard: false
        })
      );

      let project = JSON.parse(data.data);
      if (data.details) {
        const dataset = JSON.parse(data.details);
        project.settings.dataProvider = dataset;
      }
      if (data.mapData) {
        project.options.chart.map = JSON.parse(data.mapData);
        project.options.chart.map.hasOldMapData = true;
      }

      yield call(loadProject, {
        data: {
          project
        }
      });
    } else {
      throw '';
    }
  } catch (e) {
    throw new Error(e);
  }
}

function fixControlPoints(chart) {
  (chart.annotations || []).forEach(function (annotation) {
    if (annotation.labels && annotation.labels.length > 0) {
      // Is a label, add control points to it.
      annotation.labels[0].controlPoints = stockTools.getLabelControlPointsProps();
    }
  });
}

export function* loadProject(params) {
  const { project: projectData } = params.data;

  if (!projectData) return;
  const pluginConfig = loadRelevantModules(projectData);

  let templateOptions = [{}];
  let templateMeta = [];

  if (projectData.template) {
    if (isArray(projectData.template)) templateOptions = projectData.template;
    else templateOptions = [projectData.template];
  }

  if (projectData.settings?.template) {
    templateMeta = [projectData.settings?.template[0]];
  }

  let templateDataSettings = {};

  if (projectData.settings?.templateDataSettings) {
    templateDataSettings = projectData.settings.templateDataSettings;
  }

  let customizedOptions = {};
  let themeOptions = {};

  if (projectData.options) customizedOptions = projectData.options;

  setGlobalLang(customizedOptions);

  if (typeof projectData.theme !== 'undefined') {
    themeOptions = projectData.theme;
  }

  let constr = ['Chart'];

  // Support legacy format
  if (projectData.settings && projectData.settings.templateView) {
    if (projectData.settings.templateView.activeSection === 'stock') {
      constr = ['StockChart'];
    }
  }

  if (projectData.settings) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    constr = isString(projectData.settings.constructor)
      ? [projectData.settings.constructor]
      : projectData.settings.constructor;
  }

  let dataOptions = [[]];

  if (projectData.settings && projectData.settings.dataProvider) {
    const data = yield call(loadData, projectData, customizedOptions);
    if (data.csv) dataOptions = parseCSV(projectData.settings.dataProvider.csv);
  }

  fixControlPoints(customizedOptions);
  fixPlotLinesAndBands(customizedOptions);
  loadTooltip(customizedOptions);

  let isMap = projectData.options && (projectData.options.chart?.map || projectData.options.plotOptions?.map);
  const isGeoJSON = isObject(projectData.options?.chart?.map);
  if (isMap) {
    if (projectData.options?.chart?.map) {
      if (!isGeoJSON) {
        yield call(loadMap, {
          data: {
            skipRedraw: true,
            map: {
              map: projectData.options.chart.map
            }
          }
        });
      } else if (isGeoJSON) {
        yield call(loadMapCodeNameMapping, {
          data: {
            map: projectData.options.chart.map,
            code: projectData.options.chart.map.hccode,
            name: projectData.options.chart.map.hcname
          }
        });
      }
    }
  }
  yield call(setCustomCode, {
    data: {
      customCode: projectData.customCode,
      skipEmit: true
    }
  });

  yield put(
    setProjectConfigAction({
      customizedOptions,
      templateOptions,
      themeOptions: (themeOptions ?? {}).options ?? {},
      showChartWizard: false,
      templateDataSettings,
      pluginConfig,
      plugins: projectData?.settings?.plugins ?? {},
      cssModules: projectData?.settings?.plugins?.cssModules ?? [],
      dataOptions,
      templateMeta
    })
  );

  yield call(updateAggregated);

  let projectConfigOptions = yield call(fixSeriesMapping, {
    projectData,
    isMap,
    isGeoJSON
  });

  // Work out selected template from stored value
  let chosenWizardTemplate = false;

  if (templateMeta) {
    chosenWizardTemplate = yield call(getTemplateData, {
      data: {
        template: templateMeta
      }
    });
  }

  yield all([
    put(setProjectConfigAction(projectConfigOptions)),
    put(
      setChartAction({
        chosenWizardTemplate,
        constr: isMap ? 'Map' : constr[0] ?? 'Chart',
        loadingEditor: false,
        isMap
      })
    )
  ]);

  yield call(initChart);
}

export function* initEditor(params) {
  const { query, urlParams, history, isThemeEditor, defaultThemeDetails } = params.data;
  const hasChartId = urlParams.chartid && parseInt(urlParams.chartid, 10);
  const hasThemeId = urlParams.themeid && parseInt(urlParams.themeid, 10);
  const hasUUID = query.uuid && query.uuid > '';
  const isNew =
    (location.pathname.match('/create') && !hasChartId && !hasThemeId) ||
    (location.pathname.match('/create') && hasUUID);
  const isMap = 'maps' in query;

  const profileConfig = yield select(getProfileConfig);
  const { division, team, user } = profileConfig;
  const anonymous = !user && !team;

  let chartConfig = yield select(getChartConfig);

  let defaultOptions = merge({}, chartConfig.defaultChartOptions);
  if (isMap) {
    defaultOptions = merge(defaultOptions, chartConfig.defaultMapOptions);
  }

  let inspector = Inspector();
  stockTools = StockTools(true);
  stockTools.init(Highcharts);

  let projectConfig = {};
  if (isNew) {
    let customizedOptions = {}; // merge({},config.customizedOptions);

    customizedOptions = merge(merge({}, defaultOptions), customizedOptions);

    if (!customizedOptions.series) {
      customizedOptions = yield call(addBlankSeries, {
        data: {
          index: 0,
          skipEmit: true
        }
      });
    }
    projectConfig = {
      themeOptions: merge({}, chartConfig.defaultTheme),
      customizedOptions,
      dataOptions: parseCSV(isMap ? DefaultData.choropleth.data : DefaultData.line.data)
    };
  }

  const editorConfig = yield generateEditorSettings(team, division, anonymous, isThemeEditor);

  let editorState = {};
  if (isNew) {
    editorState = {
      showChartWizard: true
    };
  }

  yield all([
    put(setProjectConfigAction(projectConfig)),
    put(
      setChartAction({
        isMap,
        defaultOptions,
        editorConfig: editorConfig.editorConfig,
        loadingEditor: !isNew,
        initedEditor: true,
        inspector,
        chartId: urlParams.chartid && parseInt(urlParams.chartid, 10),
        chartVersion: urlParams.chartversion && parseInt(urlParams.chartversion, 10),
        localTeam: localStorage.getItem('editorTeam') ? JSON.parse(localStorage.getItem('editorTeam')) : null,
        division,
        dataCollection: DataCollection(),
        cmsModalOpen: 'cms' in query,
        isThemeEditor,
        ...editorState
      })
    )
  ]);

  yield call(updateAggregated);

  if (isThemeEditor) {
    if (isNew) {
      yield call(initThemeEditor, { data: { defaultThemeDetails } });
    } else if (hasThemeId) {
      yield call(loadTheme, {
        data: {
          chartId: hasThemeId,
          history
        }
      });
    }
  } else if (hasChartId) {
    yield call(loadChart, {
      data: {
        chartId: hasChartId,
        history
      }
    });
  } else if (hasUUID) {
    yield call(loadChartFromUUID, {
      data: {
        uuid: query.uuid
      }
    });
  }

  // regenerateChart();
}

/* Init the chart */
export function* initChart(pnode, noAnimation) {
  const config = yield select(getProjectConfig);
  const chartConfig = yield select(getChartConfig);
  const customizedOptions = merge({}, config.customizedOptions) ?? {};
  const aggregatedOptions = merge({}, config.aggregatedOptions) ?? {};
  const seriesTypes = getSeriesType(aggregatedOptions);

  // We want to work on a copy..
  let options = aggregatedOptions; // merge({}, options || M.aggregatedOptions);
  let addGSheetBackIn = options?.data?.googleSpreadsheetKey;
  let addLiveDataBackIn =
    options?.data && (options.data.csvURL !== '' || options.data.columnsURL !== '' || options.data.rowsURL !== '');

  if (chartConfig.inlineEditor) chartConfig.inlineEditor.destroy();
  let anim = {},
    addAnimBackIn = false;

  if (noAnimation) {
    if (options?.plotOptions?.series && 'animation' in options.plotOptions.series) {
      addAnimBackIn = true;
      if (isObject(options.plotOptions.series.animation)) {
        anim = merge({}, options.plotOptions.series.animation);
      } else anim = options.plotOptions.series.animation;
    }
    setAttr(options, 'plotOptions.series.animation', false);
  }

  try {
    const chartConstr = chartConfig.constr || 'Chart';

    if (options.data && !options.data.complete) {
      options.data.complete = function (chartOptions) {
        let templateDataSettings = config.templateDataSettings;
        const keys = Object.keys(templateDataSettings || {});
        keys.forEach(function (key) {
          const serie = chartOptions.series[key];
          (serie.data || []).forEach(function (d, i) {
            if (templateDataSettings[key].data && templateDataSettings[key].data[i]) {
              merge(d, templateDataSettings[key].data[i]);
            }
          });
        });

        if (options?.chart?.type === 'packedbubble') {
          chartOptions.series.forEach((series) => {
            series.data = series.data.filter((point) => point.value);
          });
        }
        return chartOptions;
      };
    }

    if (!options.exporting) options.exporting = {};
    options.exporting.url = 'https://api.everviz.com/export';

    Highcharts.AST.filterUserAttributes = (a) => a;
    Highcharts.AST.allowedTags.indexOf = () => '';
    Highcharts.AST.setElementHTML = (el, html) => (el.innerHTML = html);

    let wizardSmallScreenOption = getHighchartsResponsiveConfig(seriesTypes, chartConfig, options);
    merge(options, wizardSmallScreenOption);
    let chart = new Highcharts[chartConstr]('highcharts-container', options);
    let inlineEditor = Inline(chart);
    if (chartConfig?.inspector) {
      chartConfig.inspector.init(true);
    }

    Highcharts.addEvent(Highcharts.Chart, 'load', function () {
      // eslint-disable-next-line no-invalid-this, @typescript-eslint/no-empty-function
      if (this && this.credits) this.credits.on('click', function () {});
    });

    // Highcharts removes the google spreadsheet key/live data url of the passed in options for some reason.
    // Add it back in after render, these settings get passed into the advanced customise section,
    // if this key isnt in there when a property is changed, it will remove the key and wont render the chart.
    if (addGSheetBackIn && customizedOptions.data) {
      aggregatedOptions.data.googleSpreadsheetKey = customizedOptions.data.googleSpreadsheetKey;
    } else if (addLiveDataBackIn && customizedOptions.data) {
      if (customizedOptions.data.csvURL) aggregatedOptions.data.csvURL = customizedOptions.data.csvURL;
      else if (customizedOptions.data.columnsURL) aggregatedOptions.data.columnsURL = customizedOptions.data.columnsURL;
      else if (customizedOptions.data.rowsURL) aggregatedOptions.data.rowsURL = customizedOptions.data.rowsURL;
    }

    if (addAnimBackIn) {
      setAttr(options, 'plotOptions.series.animation', anim);
    } else delete options?.plotOptions?.series?.animation;

    // Want to get rid of series data for all charts.
    // Except for item charts right now, as it uses the data amount and symbol in the customize panel
    // Could need this for other things too, so improve this in future.
    if (aggregatedOptions && aggregatedOptions.chart && aggregatedOptions.chart.type !== 'item') {
      if (aggregatedOptions && aggregatedOptions.series) {
        aggregatedOptions.series.forEach(function (s) {
          s.data = [];
        });
      }
    }

    Highcharts.error = function (code, stopLoading) {
      if (stopLoading) {
        throw new Error(code);
      }
    };

    yield all([
      put(
        setChartAction({
          chart,
          inlineEditor
        })
      )
    ]);
  } catch (code) {
    const codeStr = code?.toString();
    if (codeStr?.indexOf('Highcharts error') >= 0) {
      let i1 = codeStr.indexOf('#'),
        i = codeStr.substr(i1).indexOf(':'),
        id = parseInt(codeStr.substr(i1 + 1, i), 10),
        item = highchartsErrors[id];
      if (item) {
        yield put(
          setChartAction({
            editorError: `${item.title} ${item.text}`
          })
        );
      }
    }
  }
}

export function* updateAggregated(noCustomCode) {
  const config = yield select(getProjectConfig);
  const chartConfig = yield select(getChartConfig);
  const customizedOptions = merge({}, config.customizedOptions) ?? {};
  const templateOptions = (config.templateOptions ?? []).map((c) => merge({}, c));
  const themeOptions = merge({}, config.themeOptions) ?? {};

  let aggregatedOptions = {};

  ['xAxis', 'yAxis'].forEach((key) => {
    if (customizedOptions && !isArray(customizedOptions[key]) && customizedOptions[key]) {
      customizedOptions[key] = [customizedOptions[key] || {}];
    }
  });

  let aggregatedTemplate = {};

  // Apply theme first
  if (themeOptions && Object.keys(themeOptions).length) {
    merge(aggregatedOptions, merge(merge({}, themeOptions)));
  }

  templateOptions.forEach(function (arr) {
    if (arr) {
      if (arr.yAxis && !isArray(arr.yAxis)) arr.yAxis = [arr.yAxis];
      if (arr.xAxis && !isArray(arr.xAxis)) arr.xAxis = [arr.xAxis];
      aggregatedTemplate = merge(aggregatedTemplate, arr);
    }
  });

  merge(aggregatedOptions, merge({}, config.pluginConfig));
  merge(aggregatedOptions, merge({}, aggregatedTemplate), null);

  ['xAxis', 'yAxis'].forEach((key) => {
    if (themeOptions && themeOptions[key]) {
      themeOptions[key] = isArray(themeOptions[key]) ? themeOptions[key] : [themeOptions[key]];

      if (isArray(aggregatedOptions[key])) {
        aggregatedOptions[key].forEach(function (obj, i) {
          if (i < themeOptions[key].length) {
            merge(obj, themeOptions[key][i]);
          }
        });
      }
    }

    if (aggregatedOptions[key] && aggregatedTemplate[key] && isArray(aggregatedOptions[key])) {
      aggregatedOptions[key].forEach(function (obj, i) {
        if (i < aggregatedTemplate[key].length) {
          merge(obj, aggregatedTemplate[key][i]);
        }
      });
    }
  });

  // Temporary hack
  aggregatedOptions.series = [];
  if (isArray(customizedOptions.series)) {
    customizedOptions.series.forEach(function (obj, i) {
      let mergeTarget = {};

      if (aggregatedTemplate && isArray(aggregatedTemplate.series)) {
        if (i < aggregatedTemplate.series.length) {
          mergeTarget = merge({}, aggregatedTemplate.series[i]);
        }
      }

      if (themeOptions && isArray(themeOptions.series)) {
        if (i < themeOptions.series.length) {
          mergeTarget = merge({}, themeOptions.series[i]);
        }
      }
      aggregatedOptions.series.push(merge(mergeTarget, obj));
    });
  }

  if (themeOptions && themeOptions.series) {
    if (aggregatedOptions.series) {
      aggregatedOptions.series.forEach(function (serie, i) {
        if (!serie.type && themeOptions.series[i] && themeOptions.series[i].type) {
          serie.type = themeOptions.series[i].type;
        }
      });
    }
  }

  if (customizedOptions?.chart?.map && aggregatedOptions?.chart?.map) {
    delete aggregatedOptions.chart.map;
  }

  merge(aggregatedOptions, merge({}, customizedOptions), null, {
    xAxis: 1,
    yAxis: 1,
    series: 1,
    colorAxis: 1
  });

  if (isArray(customizedOptions.series)) {
    if (!aggregatedOptions.series) aggregatedOptions.series = [];
    (customizedOptions.series || []).forEach((serie, i) => {
      if (aggregatedOptions.series[i]) {
        aggregatedOptions.series[i] = merge(merge({}, aggregatedOptions.series[i]), merge({}, serie));
      } else aggregatedOptions.series[i] = merge({}, customizedOptions.series[i]);
    });
  }

  ['yAxis', 'xAxis', 'colorAxis'].forEach((key) => {
    if (aggregatedOptions[key] && !isArray(aggregatedOptions[key])) {
      aggregatedOptions[key] = [aggregatedOptions[key]];
    }

    let customizedAxis = cloneDeep(customizedOptions[key]);
    if (customizedAxis && !isArray(customizedAxis)) {
      customizedAxis = [customizedAxis];
    }

    if (isArray(customizedAxis)) {
      if (!aggregatedOptions[key]) aggregatedOptions[key] = [];
      (customizedAxis || []).forEach((axis, i) => {
        if (aggregatedOptions[key][i]) {
          aggregatedOptions[key][i] = merge(merge({}, aggregatedOptions[key][i]), merge({}, axis));
        } else aggregatedOptions[key][i] = merge({}, customizedAxis[i]);
      });
    }

    if (!aggregatedOptions[key] && customizedAxis) aggregatedOptions[key] = merge({}, customizedAxis);

    if (isArray(aggregatedOptions[key]) && aggregatedOptions[key].length === 1) {
      aggregatedOptions[key] = aggregatedOptions[key][0];
    }
  });

  if (themeOptions && themeOptions.colorAxis) {
    aggregatedOptions.colorAxis = themeOptions.colorAxis;
  }

  if (stockTools) {
    aggregatedOptions = merge(aggregatedOptions, stockTools?.getStockToolsToolbarConfig() ?? {});
  }

  // Finally, do custom code
  let editorError = '';
  if (!noCustomCode && isFunction(chartConfig.customCode)) {
    try {
      chartConfig.customCode(aggregatedOptions, chartConfig.chart);
    } catch (e) {
      editorError = e;
      console.log('Error in Custom Code:', e);
    }
  }

  yield all([
    put(
      setProjectConfigAction({
        aggregatedOptions
      })
    ),
    put(
      setChartAction({
        editorError
      })
    )
  ]);
}

export function* redrawProject(params) {
  let { noAnimation } = params.data;
  yield call(initChart, null, noAnimation);
}

/** Watch functions */
export function* watchInitEditor() {
  yield takeEvery(actionTypes.chartEditor.initEditor, initEditor);
}
export function* watchSaveChart() {
  yield takeEvery(actionTypes.chartEditor.saveChart, saveChart);
}
export function* watchLoadProject() {
  yield takeEvery(actionTypes.chartEditor.loadProject, loadProject);
}
export function* watchLoadChart() {
  yield takeEvery(actionTypes.chartEditor.loadChart, loadChart);
}
export function* watchLoadChartFromUUID() {
  yield takeEvery(actionTypes.chartEditor.loadChartFromUUID, loadChartFromUUID);
}
export function* watchRedrawProject() {
  yield takeEvery(actionTypes.chartEditor.redrawProject, redrawProject);
}
export function* watchPublishChart() {
  yield takeEvery(actionTypes.chartEditor.publishChart, publishChart);
}

export default function* rootSaga() {
  yield all([
    watchSaveChart(),
    watchLoadProject(),
    watchInitEditor(),
    watchLoadChart(),
    watchRedrawProject(),
    watchLoadChartFromUUID(),
    watchPublishChart()
  ]);
}
